// Filters bar UI — emits to state.setFilters().

import { TAG_CONFIDENCE_THRESHOLD } from "./lib/aggregations";
import { esc } from "./lib/format";
import { PLATFORM_FAMILIES, isPlatformFamily } from "./lib/platform_families";
import { SIGNAL_DEFS } from "./lib/signals";
import { getState, setFilters, setIncludePreQual } from "./state";
import type { AIClass, DateRangePreset, Filters, Meta, StageState } from "./types";

// SGNL-193 — derive (stage, typical-probability) pairs from the bundle.
// Returns stages sorted by probability DESC so the dropdown surfaces
// the closest-to-close stages at the top — operators can scan the
// list and pick the live ones.
//
// ARCH-02 / Q-02: use MODE (most-frequent probability per stage), not
// MAX. SF stage probabilities are stage-level defaults; AEs can manually
// override on individual opps but the canonical value dominates. MAX
// gets fooled by a single optimistic AE who sets probability=99% on a
// Discovery opp. Mode picks the default reliably.
// ARCH-03: secondary sort by stage name so equal-probability stages
// have a deterministic A-Z order.
export function stagesWithProbability(
  meta: Meta,
): Array<{ stage: string; prob: number | null }> {
  const opps = getState().data?.opps ?? [];
  // Per-stage: count occurrences of each probability value.
  const counts = new Map<string, Map<number, number>>();
  for (const o of opps) {
    if (!o.stage || o.probability == null) continue;
    let pm = counts.get(o.stage);
    if (!pm) {
      pm = new Map();
      counts.set(o.stage, pm);
    }
    pm.set(o.probability, (pm.get(o.probability) ?? 0) + 1);
  }
  // Pick mode per stage. On a tie, take the LOWER probability (more
  // conservative — early-stage opps over later-stage anomalies).
  const byStage = new Map<string, number>();
  for (const [stage, pm] of counts) {
    let bestProb = -1;
    let bestCount = -1;
    for (const [prob, count] of pm) {
      if (count > bestCount || (count === bestCount && prob < bestProb)) {
        bestProb = prob;
        bestCount = count;
      }
    }
    if (bestProb >= 0) byStage.set(stage, bestProb);
  }
  return meta.stages
    .map((s) => ({ stage: s, prob: byStage.get(s) ?? null }))
    .sort((a, b) => {
      // Stages without a probability fall to the bottom.
      const av = a.prob ?? -1;
      const bv = b.prob ?? -1;
      return bv !== av ? bv - av : a.stage.localeCompare(b.stage);
    });
}

// security-review M-1: `esc()` makes a value safe inside an HTML
// attribute but NOT inside a CSS-attribute-selector value. Stage
// picklist values containing `/`, `[`, `]`, `(`, `:` (real SF
// values like "Proposal/Negotiation") would break Playwright /
// querySelector selectors built off `data-testid`. Sanitize to a
// stable alphanumeric+hyphen slug for the testid only; the
// visible label + `data-stage` keep the unmodified value.
function stageTestidSlug(s: string): string {
  return s.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase() || "blank";
}

// SGNL-249 — Render the chip buttons for the stage filter.
// Source = meta.stages ∪ filters.stages so a URL/bookmark carrying
// a stage that has since dropped out of the SF picklist still
// renders a (pressed) chip (architect-review F-3): otherwise the
// active-filter badge counts a "ghost" stage with nothing visibly
// pressed. Order: known meta stages first (probability-sorted),
// unknown stages appended at the bottom alphabetical.
export function renderStageChips(meta: Meta, f: Filters): string[] {
  const known = stagesWithProbability(meta);
  const knownSet = new Set(known.map((sp) => sp.stage));
  // PR-Agent #163 round-2 (importance 4): precompute selectedSet once
  // so per-chip "is pressed" is O(1) and dedupes any accidental
  // duplicates in `f.stages` before deriving the unknown list.
  const selectedSet = new Set(f.stages ?? []);
  const unknown = Array.from(selectedSet)
    .filter((s) => !knownSet.has(s))
    .sort()
    .map((s) => ({ stage: s, prob: null as number | null }));
  const all = [...known, ...unknown];
  // PR-Agent #163 (importance 4): two different stage strings can
  // slug to the same testid (e.g. "Closed/Won" + "Closed-Won" →
  // "closed-won"). Numeric suffix on collision keeps the common
  // case unchanged while guaranteeing per-render uniqueness.
  const seen = new Map<string, number>();
  return all.map((sp) => {
    const on = selectedSet.has(sp.stage);
    const label = sp.prob != null ? `${sp.stage} ${sp.prob}%` : sp.stage;
    const base = stageTestidSlug(sp.stage);
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    const testid = n === 1 ? base : `${base}-${n}`;
    return `<button type="button"
      class="rc-chip${on ? " rc-chip--on" : ""}"
      data-key="stage-chip"
      data-stage="${esc(sp.stage)}"
      data-testid="stage-chip-${testid}"
      aria-pressed="${on ? "true" : "false"}">${esc(label)}</button>`;
  });
}


// SGNL-26 — derive distinct tag_keys (verdict='yes' AND confidence above
// threshold) from the current data bundle so the filter chip row only
// surfaces tags that would actually match if selected.
function distinctTagKeys(): string[] {
  const opps = getState().data?.opps ?? [];
  const keys = new Set<string>();
  for (const o of opps) {
    for (const t of o.tags ?? []) {
      if (t.verdict === "yes" && t.confidence >= TAG_CONFIDENCE_THRESHOLD) {
        keys.add(t.tag_key);
      }
    }
  }
  return [...keys].sort();
}

// SGNL-109 — chip selected/unselected is now state-based (filled+✓ vs
// outlined), not verdict-coloured. The `conf ≥` input was dropped —
// operators don't reason about confidence; the threshold is pinned in
// TAG_CONFIDENCE_THRESHOLD.
function renderTagFilter(f: Filters): string {
  // SGNL-109 / SGNL-111 — union the corpus tag keys with any keys
  // already in f.tagKeys so an obsolete URL deep-link key still renders
  // a (selected) chip the operator can click to remove. Without this
  // an "invisible" active filter excludes every opp with no chip to
  // clear — superseded SGNL-111's banner proposal with a tighter fix.
  const corpusKeys = distinctTagKeys();
  const selected = new Set(f.tagKeys ?? []);
  const keys = [...new Set([...corpusKeys, ...selected])].sort();
  if (keys.length === 0) return ""; // No tags + no selection — hide the row.
  return `
    <div class="filters tag-filter" data-testid="tag-filter" style="margin-top:8px; display:flex; align-items:center; gap:6px; flex-wrap:wrap;">
      <span class="muted" style="font-size:12px;">Tags:</span>
      ${keys
        .map((k) => {
          const isOn = selected.has(k);
          return `
        <button type="button"
                class="tag-chip${isOn ? " is-selected" : ""}"
                aria-pressed="${isOn}"
                data-tag-key="${esc(k)}"
                data-testid="tag-filter-chip">
          ${isOn ? "✓ " : ""}${esc(k)}
        </button>`;
        })
        .join("")}
    </div>`;
}

// Date presets with dynamic year labels. Computed at render time so labels
// don't go stale across calendar years. cy0/cy1/cy2 are calendar-year
// shorthand; ytd/q1 always reference the CURRENT year.
// SGNL-153 — Health-signal filter chips. Exported so hygiene.ts can render
// them inside the Data Hygiene tab (SGNL-176: moved from global filter bar).
export function renderSignalFilter(f: Filters): string {
  const selected = new Set(f.activeSignals ?? []);
  return `
    <div class="tag-filter" data-testid="signal-filter-bar" style="margin-top:4px;">
      <span class="muted" style="font-size:0.85em; margin-right:6px;">Health signals:</span>
      ${SIGNAL_DEFS.map(
        ({ slug, label }) =>
          `<button type="button" class="chip${selected.has(slug) ? " aqua" : ""}" ` +
          `style="font-size:0.8em; margin:2px;" ` +
          `aria-pressed="${selected.has(slug)}" ` +
          `data-testid="signal-filter-chip" data-signal="${esc(slug)}"` +
          `>${selected.has(slug) ? "✓ " : ""}${esc(label)}</button>`,
      ).join("")}
    </div>`;
}

function buildDatePresets(today: Date = new Date()): { value: DateRangePreset; label: string }[] {
  const y = today.getUTCFullYear();
  return [
    { value: "ytd", label: `YTD ${y} (Jan–today)` },
    { value: "cy0", label: `${y} (full year)` },
    { value: "open-cy0", label: `Active ${y}+ (close date ≥ Jan 1)` },
    { value: "q1", label: `Q1 ${y} (Jan–Mar)` },
    { value: "r12", label: "Rolling 12 months" },
    { value: "cy1", label: `${y - 1}` },
    { value: "cy2", label: `${y - 2}` },
    { value: "all", label: "All dates" },
    { value: "custom", label: "Custom…" },
  ];
}

/** Bind click handlers for signal filter chips in any host element.
 * Extracted from renderFiltersBar so hygiene.ts can call it after rendering
 * signal chips inside the tab — the chips live in a different DOM host than
 * the global filter bar (SGNL-176 ARCH-03). */
export function bindSignalFilterHandlers(host: HTMLElement): void {
  for (const btn of host.querySelectorAll<HTMLButtonElement>(
    '[data-testid="signal-filter-chip"]',
  )) {
    btn.addEventListener("click", () => {
      const slug = btn.dataset.signal;
      if (!slug) return;
      const current = new Set(getState().filters.activeSignals ?? []);
      if (current.has(slug)) current.delete(slug);
      else current.add(slug);
      setFilters({ activeSignals: current.size > 0 ? [...current] : undefined });
    });
  }
}

/** SGNL-209 — keys counted toward the "X filters active" badge.
 *
 * Excluded by design:
 *   - `dateRange` / `dateFrom` / `dateTo`: the date window is always
 *     active (defaults to cy0), so counting it would always be ≥1 and
 *     drown the signal we actually want.
 *   - `tagKeys` / `activeSignals`: chip-driven filters have their own
 *     visual state on the chip itself.
 * Counted: bu / stages / type / owner / q / min_amount / aiClass /
 * aiMaturity / state / probability_min / probability_max.
 *
 * SGNL-249: `stages` (multi-select array) counts as active only when
 * non-empty. `setFilters` strips `[]` to undefined, so in normal flow
 * this loop never sees `stages: []`; the explicit guard below is
 * defense-in-depth in case a caller hands a transient `[]` straight
 * to `countActiveFilters` (e.g. mid-toggle render). */
const COUNTED_FILTER_KEYS: ReadonlyArray<keyof Filters> = Object.freeze([
  "bu", "stages", "type", "owner", "q", "account", "min_amount",
  "aiClass", "aiMaturity", "state",
  "probability_min", "probability_max",
  // SGNL-267 — only counts when non-default ("closed_out"); the handler stores
  // undefined for the "close" default so it never counts on the common path.
  "dateBasis",
  "opportunitySource", // SGNL-268
  "platform", // Platform tagging
  "platformFamily",
]);

export function countActiveFilters(f: Filters): number {
  let n = 0;
  for (const k of COUNTED_FILTER_KEYS) {
    const v = f[k];
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v) && v.length === 0) continue;
    n++;
  }
  return n;
}

/** Map of which Filters key is bound to each `data-key="…"` input in
 *  the filter bar. Drives the Purple Rain highlight on inputs that
 *  hold a non-default value (SGNL-209). */
const FILTER_KEY_BY_DATA_KEY: Readonly<Record<string, keyof Filters>> = Object.freeze({
  // SGNL-249 — `stage` removed; the multi-select chip list has its
  // own visual selected-state (`.rc-chip--on`) so no input-level
  // highlight is needed.
  bu: "bu", type: "type", owner: "owner",
  q: "q", account: "account", min_amount: "min_amount",
  aiClass: "aiClass", aiMaturity: "aiMaturity", state: "state",
  dateBasis: "dateBasis", // SGNL-267
  opportunitySource: "opportunitySource", // SGNL-268
  platform: "platform", platformFamily: "platformFamily", // Platform tagging
});

/** SGNL-209 — capture the focused-input identity + selection range so a
 *  re-render of the filter bar doesn't drop focus. Without this, typing
 *  the 3rd char into the search input loses focus (the prior char
 *  triggered setFilters → renderEverything → renderFiltersBar →
 *  innerHTML wipes the input). */
interface FocusSnapshot {
  dataKey: string;
  selectionStart: number | null;
  selectionEnd: number | null;
  selectionDirection: "forward" | "backward" | "none" | null;
}

function captureFocusSnapshot(host: HTMLElement): FocusSnapshot | null {
  const active = host.ownerDocument.activeElement as HTMLInputElement | null;
  if (!active || !host.contains(active)) return null;
  const key = active.dataset.key;
  if (!key) return null;
  // Only text-shaped inputs carry a useful selection range. Number
  // inputs don't (browsers throw if you read .selectionStart on them);
  // we still restore focus, just without a range.
  const isTextish = active.type === "search" || active.type === "text";
  return {
    dataKey: key,
    selectionStart: isTextish ? active.selectionStart : null,
    selectionEnd: isTextish ? active.selectionEnd : null,
    selectionDirection: isTextish ? (active.selectionDirection ?? null) : null,
  };
}

function restoreFocusSnapshot(host: HTMLElement, snap: FocusSnapshot | null): void {
  if (!snap) return;
  // CSS.escape is available in browsers but not jsdom — fall back to
  // raw interpolation. Safe here because data-key values are a closed
  // set of compile-time identifiers (bu, stage, q, …); never user
  // input. Defence in depth: pre-validate the slug to ASCII word chars
  // (security review LOW — explicit ASCII intent vs JS `\w` Unicode
  // semantics) so a future test that smuggles arbitrary data via
  // dataset can't break out of the attribute boundary.
  if (!/^[A-Za-z0-9_]+$/.test(snap.dataKey)) return;
  // Architect MED — widen from `input[data-key=...]` to `[data-key=...]`
  // so focus on a <select> survives a re-render too. Today the search
  // input is the only debounce-driven re-render trigger; broadening
  // the selector is future-proofing rather than a current-bug fix.
  const target = host.querySelector<HTMLInputElement | HTMLSelectElement>(
    `[data-key="${snap.dataKey}"]`,
  );
  if (!target) return;
  target.focus();
  // Restore the caret position so the user keeps typing where they
  // left off rather than at the start/end of the value. setSelectionRange
  // only exists on text-shaped HTMLInputElements; the architect-MED
  // widening to also restore <select> focus means we type-narrow before
  // calling.
  if (
    target instanceof HTMLInputElement &&
    snap.selectionStart !== null &&
    snap.selectionEnd !== null
  ) {
    try {
      target.setSelectionRange(
        snap.selectionStart,
        snap.selectionEnd,
        snap.selectionDirection ?? "forward",
      );
    } catch {
      // Some input types throw on setSelectionRange. Focus is already
      // restored; silent fallback.
    }
  }
}

/** SGNL-209 — single source of truth for "what does Clear reset".
 *
 * Architect MED: previously the badge proxied to the Clear button via
 * a programmatic `.click()`. Two problems with that: (a) it fires any
 * future side-effect listeners on the Clear button as if the badge
 * were the source, and (b) inconsistent with the codebase's named-
 * handler pattern.
 *
 * Code-reviewer HIGH (pre-existing): aiMaturity was missing from the
 * previous Clear payload — operator could pick "Stage 3" → Clear → the
 * select re-renders blank but state.filters.aiMaturity still held the
 * value, so the badge would still read "1 filter active". Now included. */
function clearAllFilters(): void {
  setFilters({
    bu: undefined,
    stages: undefined,
    type: undefined,
    owner: undefined,
    opportunitySource: undefined, // SGNL-268
    platform: undefined, // Platform tagging
    platformFamily: undefined,
    q: undefined,
    // SGNL-265 — reset the account-only search alongside the others.
    account: undefined,
    min_amount: undefined,
    // Q-06: include the SGNL-193 filter in the Clear handler so
    // Focus-list state is fully reset alongside the other knobs.
    probability_min: undefined,
    // SGNL-209: matching upper bound from the configurable Focus list band.
    probability_max: undefined,
    // Clear resets to cy0 (full calendar year) to match initial-load
    // default. Matches state.ts readFiltersFromUrl() which sets cy0 on
    // first visit. Previously used "ytd" (Jan 1 – today) which
    // incorrectly hid all opps with future close dates.
    dateRange: "cy0",
    dateFrom: undefined,
    dateTo: undefined,
    dateBasis: undefined, // SGNL-267 — back to close-date basis
    aiClass: undefined,
    // SGNL-209 code-reviewer HIGH: aiMaturity was missing from the
    // pre-fix payload; now reset alongside its sibling selects.
    aiMaturity: undefined,
    state: undefined,
    tagKeys: undefined,
    activeSignals: undefined,
  });
}

export function renderFiltersBar(host: HTMLElement, meta: Meta): void {
  const f = getState().filters;
  const activePreset: DateRangePreset = f.dateRange ?? "cy0";
  const customVisible = activePreset === "custom";
  const datePresets = buildDatePresets();
  // SGNL-209: capture focus + caret BEFORE the innerHTML wipe; restore
  // after. Without this the search input loses focus after every
  // keystroke once the 200ms debounce fires setFilters.
  const focusSnap = captureFocusSnapshot(host);
  const activeCount = countActiveFilters(f);
  host.innerHTML = `
    <div class="filters" data-testid="filters">
      <!-- SGNL-209: badge is always rendered (with display:none when
           inactive) so the surrounding role="status" / aria-live="polite"
           wrapper has a stable text node to announce count changes via.
           Without this, removing/re-inserting the button would re-mount
           the live region on each transition and skip the announcement. -->
      <div role="status" aria-live="polite" class="filters-active-region">
        ${activeCount > 0
          ? `<button type="button" class="filters-active-badge"
                     data-action="clear-via-badge"
                     data-testid="filters-active-badge"
                     aria-label="${activeCount} filter${activeCount === 1 ? "" : "s"} active — click to clear all"
                     title="Click to clear all filters">
              ${activeCount} filter${activeCount === 1 ? "" : "s"} active
            </button>`
          : ""}
      </div>
      <select aria-label="Date basis" data-key="dateBasis" data-testid="date-basis">
        <option value="close" ${(f.dateBasis ?? "close") === "close" ? "selected" : ""}>Close date</option>
        <option value="closed_out" ${f.dateBasis === "closed_out" ? "selected" : ""}>Closed-out date</option>
      </select>
      <select aria-label="Date range" data-key="dateRange" data-testid="date-range">
        ${datePresets.map(
          (p) => `<option value="${p.value}" ${activePreset === p.value ? "selected" : ""}>${p.label}</option>`,
        ).join("")}
      </select>
      <input type="date" aria-label="From date" data-key="dateFrom" data-testid="date-from"
        value="${esc(f.dateFrom)}" ${customVisible ? "" : "hidden"} />
      <input type="date" aria-label="To date" data-key="dateTo" data-testid="date-to"
        value="${esc(f.dateTo)}" ${customVisible ? "" : "hidden"} />
      <select aria-label="Business Unit" data-key="bu">
        <option value="">All BUs</option>
        ${meta.bus.map((b) => `<option value="${esc(b)}" ${f.bu === b ? "selected" : ""}>${esc(b)}</option>`).join("")}
      </select>
      <!-- SGNL-249 — Stage filter as a multi-select chip list.
           Reuses the .rc-chips / .rc-chips__legend pattern from
           the Revenue Calendar threshold chips so the legend lays
           out predictably inside the filter-bar flex row (browsers
           render legend-inside-flex inconsistently — use a span
           sibling instead). Stage source = meta.stages ∪
           filters.stages so a URL carrying an obsolete picklist
           value still renders a pressed chip operators can clear
           (architect-review F-3 ghost-filter mitigation). -->
      <div class="rc-chips" data-testid="stage-chip-list" role="group" aria-label="Stages">
        <span class="rc-chips__legend">Stages</span>
        ${renderStageChips(meta, f).join("")}
      </div>
      <select aria-label="Type" data-key="type">
        <option value="">All Types</option>
        ${meta.types.map((t) => `<option value="${esc(t)}" ${f.type === t ? "selected" : ""}>${esc(t)}</option>`).join("")}
      </select>
      <select aria-label="Owner" data-key="owner">
        <option value="">All Owners</option>
        ${meta.owners.map((o) => `<option value="${esc(o)}" ${f.owner === o ? "selected" : ""}>${esc(o)}</option>`).join("")}
      </select>
      <select aria-label="Opportunity Source" data-key="opportunitySource" data-testid="opp-source">
        <option value="">All Sources</option>
        ${(meta.opportunity_sources ?? [])
          .map((s) => `<option value="${esc(s)}" ${f.opportunitySource === s ? "selected" : ""}>${esc(s)}</option>`)
          .join("")}
      </select>
      <!-- Platform tagging — two independent dropdowns. "Platform" picks one
           exact Project_Platform__c member; "Platform Family" rolls every
           Adobe/Salesforce/… product up to its vendor. Both can be active. -->
      <select aria-label="Platform" data-key="platform" data-testid="platform">
        <option value="">All Platforms</option>
        ${(meta.project_platforms ?? [])
          .map((p) => `<option value="${esc(p)}" ${f.platform === p ? "selected" : ""}>${esc(p)}</option>`)
          .join("")}
      </select>
      <select aria-label="Platform family" data-key="platformFamily" data-testid="platform-family">
        <option value="">All Platform Families</option>
        ${PLATFORM_FAMILIES.map(
          (fam) => `<option value="${esc(fam)}" ${f.platformFamily === fam ? "selected" : ""}>${esc(fam)}</option>`,
        ).join("")}
      </select>
      <select aria-label="AI class" data-key="aiClass">
        <option value="">All AI types</option>
        <optgroup label="Rep classification">
          <option value="selling" ${f.aiClass === "selling" ? "selected" : ""}>Selling AI (rep)</option>
          <option value="using" ${f.aiClass === "using" ? "selected" : ""}>Using AI (rep)</option>
          <option value="non" ${f.aiClass === "non" ? "selected" : ""}>Non-AI (rep)</option>
        </optgroup>
        <!-- SGNL-127 — LLM-derived verdicts surface as sibling options
             so an operator can flip Rep ↔ LLM with one menu pick.
             "Disagree" is the union of (rep says X, LLM says ¬X) — the
             audit-and-write-back candidates for SGNL-6 / R6. -->
        <optgroup label="LLM classification">
          <option value="selling-llm" ${f.aiClass === "selling-llm" ? "selected" : ""}>Selling AI (LLM)</option>
          <option value="using-llm" ${f.aiClass === "using-llm" ? "selected" : ""}>Using AI (LLM)</option>
          <option value="non-llm" ${f.aiClass === "non-llm" ? "selected" : ""}>Non-AI (LLM)</option>
          <option value="disagree" ${f.aiClass === "disagree" ? "selected" : ""}>Rep ↔ LLM disagree</option>
        </optgroup>
      </select>
      <!-- SGNL-102 — AI Maturity dropdown surfaces the existing
           f.aiMaturity filter (was state-only, set by histogram drill
           clicks). Stages match the canonical 5-bucket convention used
           in views/maturity.ts; "Unset" maps to a non-stage prefix
           filter that excludes any rep-set value. The filter is
           prefix-based (Stage N matches "Stage N: Transform" too) so
           legacy ai_maturity values like "Stage 1: Enhance" still bin
           correctly. -->
      <select aria-label="AI Maturity" data-key="aiMaturity">
        <option value="">All AI Maturities</option>
        <option value="Stage 1" ${f.aiMaturity === "Stage 1" ? "selected" : ""}>Stage 1 — Enhance</option>
        <option value="Stage 2" ${f.aiMaturity === "Stage 2" ? "selected" : ""}>Stage 2 — Extend</option>
        <option value="Stage 3" ${f.aiMaturity === "Stage 3" ? "selected" : ""}>Stage 3 — Transform</option>
        <option value="Stage 4" ${f.aiMaturity === "Stage 4" ? "selected" : ""}>Stage 4 — Reinvent</option>
        <option value="Stage 5" ${f.aiMaturity === "Stage 5" ? "selected" : ""}>Stage 5 — Revolutionize</option>
      </select>
      <select aria-label="Stage state" data-key="state">
        <option value="">Any state</option>
        <option value="won" ${f.state === "won" ? "selected" : ""}>Won</option>
        <option value="lost" ${f.state === "lost" ? "selected" : ""}>Lost</option>
        <option value="open" ${f.state === "open" ? "selected" : ""}>Open</option>
      </select>
      <!-- SGNL-265: placeholder no longer claims "account" — that now has
           its own dedicated field below. esc() guards reflected XSS from
           a ?q= URL param interpolated into innerHTML. -->
      <input type="search" placeholder="Search name / owner" aria-label="Search by opportunity name or owner" data-key="q" value="${esc(f.q)}" />
      <!-- SGNL-265 — account-only search. Separate from the general search
           above so an account lookup isn't drowned by opp-name substring
           noise (e.g. "nab" matches every "AI-Enabled" deal). -->
      <input type="search" placeholder="Account" aria-label="Search by account" data-key="account" data-testid="account-search" value="${esc(f.account)}" />
      <input type="number" placeholder="min $ amount" data-key="min_amount" value="${esc(f.min_amount)}" min="0" step="10000" />
      <!-- SGNL-228 — pre-qualification (probability < 5%) toggle. Default
           OFF: KPIs + analytics show qualifying+ pipeline only. Toggle
           ON re-fetches /api/data?include_pre_qual=true and brings back
           MQL / Pre-Qual / Stage 0 deals. Hygiene tab always sees the
           full corpus regardless (BE bypass). -->
      <label class="pre-qual-toggle"
             title="Show MQL / Pre-Qual / Stage 0 deals (probability < 5%)"
             data-testid="pre-qual-toggle-label">
        <input type="checkbox"
               data-action="toggle-pre-qual"
               data-testid="pre-qual-toggle"
               ${getState().includePreQual ? "checked" : ""} />
        <span>Show pre-qual</span>
      </label>
      <button type="button" data-action="clear">Clear</button>
    </div>
    ${renderTagFilter(f)}
  `;
  const handlers = {
    bu: (v: string) => setFilters({ bu: v || undefined }),
    type: (v: string) => setFilters({ type: v || undefined }),
    owner: (v: string) => setFilters({ owner: v || undefined }),
    opportunitySource: (v: string) => setFilters({ opportunitySource: v || undefined }), // SGNL-268
    // Platform tagging — independent; setting one never clears the other.
    platform: (v: string) => setFilters({ platform: v || undefined }),
    platformFamily: (v: string) =>
      setFilters({ platformFamily: isPlatformFamily(v) ? v : undefined }),
    q: (v: string) => setFilters({ q: v || undefined }),
    account: (v: string) => setFilters({ account: v || undefined }),
    min_amount: (v: string) => setFilters({ min_amount: v ? Number(v) : undefined }),
    dateRange: (v: string) => {
      const preset = (v || "all") as DateRangePreset;
      // Clear custom dates when leaving custom mode so they don't ghost-apply.
      const patch: Parameters<typeof setFilters>[0] =
        preset === "custom"
          ? { dateRange: preset }
          : { dateRange: preset, dateFrom: undefined, dateTo: undefined };
      setFilters(patch);
    },
    dateFrom: (v: string) => setFilters({ dateFrom: v || undefined }),
    dateTo: (v: string) => setFilters({ dateTo: v || undefined }),
    // SGNL-267 — store undefined for the "close" default so it stays off the
    // URL and out of the active-filter count on the common path.
    dateBasis: (v: string) => setFilters({ dateBasis: v === "closed_out" ? "closed_out" : undefined }),
    aiClass: (v: string) => setFilters({ aiClass: (v || undefined) as AIClass | undefined }),
    aiMaturity: (v: string) => setFilters({ aiMaturity: v || undefined }),
    state: (v: string) => setFilters({ state: (v || undefined) as StageState | undefined }),
  };
  for (const el of host.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-key]")) {
    el.addEventListener("change", () => {
      const key = el.dataset.key as keyof typeof handlers;
      handlers[key]?.(el.value);
    });
    if (el instanceof HTMLInputElement && el.type === "search") {
      el.addEventListener("input", () => {
        clearTimeout((el as unknown as { _t?: number })._t);
        (el as unknown as { _t?: number })._t = window.setTimeout(() => {
          // SGNL-265: dispatch to the input's OWN filter key. Previously
          // hardcoded to `q`, which meant every type="search" input (now
          // including the account box) wrote `q` during live typing — the
          // account filter only fired on blur/Enter via the `change`
          // handler. Route through the same `handlers` map as `change`.
          const key = el.dataset.key as keyof typeof handlers;
          handlers[key]?.(el.value);
        }, 200);
      });
    }
  }
  host.querySelector<HTMLButtonElement>('[data-action="clear"]')?.addEventListener("click", clearAllFilters);
  // SGNL-228 — pre-qual toggle. Flips state.includePreQual; main.ts's
  // subscriber detects the change and re-fetches the bundle with the
  // new ?include_pre_qual=... query param. Per-toggle re-fetch is
  // acceptable — bundle size is small (a few MB gzipped) and the BE
  // filter is the source of truth (no FE-side over-fetch then filter).
  host
    .querySelector<HTMLInputElement>('[data-action="toggle-pre-qual"]')
    ?.addEventListener("change", (e) => {
      const checked = (e.target as HTMLInputElement).checked;
      setIncludePreQual(checked);
    });
  // SGNL-249 — stage chip click handlers. Toggle the clicked stage
  // in/out of `filters.stages`; setting to `undefined` when the list
  // empties so the same "no stages selected" representation as a
  // fresh URL is restored.
  for (const btn of host.querySelectorAll<HTMLButtonElement>(
    '[data-key="stage-chip"]',
  )) {
    btn.addEventListener("click", () => {
      // PR-Agent #163 round-2 (importance 3): trim defensively so
      // whitespace-only `data-stage` values can't slip into state +
      // URL. Keeps comparisons consistent with the CSV-read path
      // which already trims each token.
      const stage = (btn.dataset.stage ?? "").trim();
      if (!stage) return;
      const current = new Set(getState().filters.stages ?? []);
      if (current.has(stage)) current.delete(stage);
      else current.add(stage);
      setFilters({ stages: current.size > 0 ? [...current] : undefined });
    });
  }
  // SGNL-153 — signal filter chip click handlers.
  bindSignalFilterHandlers(host);
  // SGNL-26 — tag chip click handlers (toggle in/out of selection).
  for (const btn of host.querySelectorAll<HTMLButtonElement>(
    '[data-testid="tag-filter-chip"]',
  )) {
    btn.addEventListener("click", () => {
      const key = btn.dataset.tagKey;
      if (!key) return;
      const current = new Set(getState().filters.tagKeys ?? []);
      if (current.has(key)) current.delete(key);
      else current.add(key);
      setFilters({ tagKeys: current.size > 0 ? [...current] : undefined });
    });
  }

  // SGNL-209 — highlight every input whose value is non-default with
  // a Purple Rain accent border so operators can see at a glance which
  // knobs are pulled.
  for (const el of host.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-key]")) {
    const dk = el.dataset.key;
    if (!dk) continue;
    const filterKey = FILTER_KEY_BY_DATA_KEY[dk];
    if (!filterKey) continue;
    const v = f[filterKey];
    const isActive = v !== undefined && v !== null && v !== "";
    el.classList.toggle("filter-input--active", isActive);
  }

  // SGNL-209 — "Clear all" CTA inside the badge. Both Clear surfaces
  // call the same named handler (architect MED: no `.click()` proxy).
  host
    .querySelector<HTMLButtonElement>('[data-action="clear-via-badge"]')
    ?.addEventListener("click", clearAllFilters);

  // SGNL-209 — restore the captured input focus + caret position AFTER
  // the innerHTML wipe so the user keeps typing where they left off.
  // Without this, the 200ms search debounce fires setFilters → re-render
  // → input recreated → focus lost after every keystroke past the first.
  restoreFocusSnapshot(host, focusSnap);
}
